import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["default"];
import { parse, parseCurried } from './parser';
import { mapIfExistsOrThrow, tryOrElse } from 'crm-index-visualization-core/core/error/errorUtils';
import { generateHash } from 'crm-index-visualization-core/core/model/hash';
import * as t from 'ts-schema';
import { segmentSchema, filterGroupSchema } from './schemas';
import { migrateFilters } from './legacy-adapters';
import { datesHash } from '../util/storageMangager';
import I18n from 'I18n';
/**
 * Represents filtering, sorting, and search criteria for a dataset.
 *
 * ## Primary Usage
 * Use `crmSearchFilters` for CRM Search API calls - it combines all
 * filters (advanced, quick, pipeline) into the proper API format without cross-object filters.
 *
 * ## Key Operations
 * - **Get CRM Search filters**: `segment.crmSearchFilters`
 * - **Get all filters (with XO)**: `segment.consolidatedFilters({}, undefined, true)`
 * - **Load from backend**: `Segment.fromBEView(backendView)`
 * - **Update immutably**: `segment.with({ search: 'new term' })`
 * - **Use with hooks**: `useSegment()` and `useMutateSegment()`
 */
export class Segment {
  constructor(init = {}) {
    var _ref, _ref2, _init$_advancedFilter, _init$advancedFilterG, _init$pipelinePropert, _init$quickFilters, _init$quickFilterProp, _init$sorts, _init$objectTypeId;
    // Initialize _advancedFilterGroups with XOFilterGroup structure
    this._advancedFilterGroups = (_ref = (_ref2 = (_init$_advancedFilter = init._advancedFilterGroups) !== null && _init$_advancedFilter !== void 0 ? _init$_advancedFilter : init.xoFilterGroups) !== null && _ref2 !== void 0 ? _ref2 : (_init$advancedFilterG = init.advancedFilterGroups) === null || _init$advancedFilterG === void 0 ? void 0 : _init$advancedFilterG.map(group => Object.assign({}, group, {
      crossObjectFilters: []
    }))) !== null && _ref !== void 0 ? _ref : [];
    this.pipelinePropertyName = (_init$pipelinePropert = init.pipelinePropertyName) !== null && _init$pipelinePropert !== void 0 ? _init$pipelinePropert : 'hs_pipeline';
    this.quickFilters = (_init$quickFilters = init.quickFilters) !== null && _init$quickFilters !== void 0 ? _init$quickFilters : [];
    this.quickFilterProperties = (_init$quickFilterProp = init.quickFilterProperties) !== null && _init$quickFilterProp !== void 0 ? _init$quickFilterProp : [];
    this.search = init.search;
    this.pipelineId = init.pipelineId;
    this.sorts = (_init$sorts = init.sorts) !== null && _init$sorts !== void 0 ? _init$sorts : [];
    this.objectTypeId = (_init$objectTypeId = init.objectTypeId) !== null && _init$objectTypeId !== void 0 ? _init$objectTypeId : '0-3';
  }
  /**
   * Getter for backward compatibility - returns the filter groups without cross-object filters
   */
  get advancedFilterGroups() {
    return this._advancedFilterGroups.map(({
      filters
    }) => ({
      filters
    }));
  }

  /**
   * Getter for the full XOFilterGroup structure
   */
  get xoFilterGroups() {
    return this._advancedFilterGroups;
  }

  /**
   * Determines whether to include pipeline filter for this object type.
   * Tasks, contacts, and companies don't use traditional pipelines.
   */
  get shouldIncludePipelineFilter() {
    const CONTACT_TYPE_ID = '0-1';
    const COMPANY_TYPE_ID = '0-2';
    const TASK_TYPE_ID = '0-14';
    return Boolean(![TASK_TYPE_ID, CONTACT_TYPE_ID, COMPANY_TYPE_ID].includes(this.objectTypeId) && this.pipelineId && this.pipelinePropertyName);
  }

  /**
   * Creates a new Segment with updated properties.
   * This is the recommended way to modify a Segment to ensure immutability.
   *
   * @example
   * // Update search term and pipeline ID
   * const newSegment = segment.with({
   *   search: 'new search term',
   *   pipelineId: '123'
   * });
   *
   * @example
   * // Clear all filters but keep sorting
   * const clearedSegment = segment.with({
   *   advancedFilterGroups: [],
   *   quickFilters: [],
   *   search: ''
   * });
   *
   * @example
   * // Add a new quick filter
   * const newQuickFilter = { property: 'hs_owner_id', operator: 'EQ', value: 'me' };
   * const segmentWithFilter = segment.with({
   *   quickFilters: [...segment.quickFilters, newQuickFilter]
   * });
   *
   * @param updates Partial updates to apply to the segment
   * @returns A new Segment instance with the updates applied
   */
  with(updates) {
    var _updates$xoFilterGrou, _updates$advancedFilt, _updates$quickFilters, _updates$search, _updates$pipelineId, _updates$quickFilterP, _updates$sorts, _updates$pipelineProp, _updates$objectTypeId;
    // Handle both xoFilterGroups and advancedFilterGroups updates
    const newFilterGroups = (_updates$xoFilterGrou = updates.xoFilterGroups) !== null && _updates$xoFilterGrou !== void 0 ? _updates$xoFilterGrou : (_updates$advancedFilt = updates.advancedFilterGroups) === null || _updates$advancedFilt === void 0 ? void 0 : _updates$advancedFilt.map(fg => ({
      filters: fg.filters,
      crossObjectFilters: []
    }));

    // Preserve existing advanced filter groups if no filter updates provided
    const finalFilterGroups = newFilterGroups !== null && newFilterGroups !== void 0 ? newFilterGroups : this._advancedFilterGroups;
    return new Segment({
      xoFilterGroups: finalFilterGroups,
      quickFilters: (_updates$quickFilters = updates.quickFilters) !== null && _updates$quickFilters !== void 0 ? _updates$quickFilters : this.quickFilters,
      search: (_updates$search = updates.search) !== null && _updates$search !== void 0 ? _updates$search : this.search,
      pipelineId: updates.pipelineId === null ? null : (_updates$pipelineId = updates.pipelineId) !== null && _updates$pipelineId !== void 0 ? _updates$pipelineId : this.pipelineId,
      quickFilterProperties: (_updates$quickFilterP = updates.quickFilterProperties) !== null && _updates$quickFilterP !== void 0 ? _updates$quickFilterP : this.quickFilterProperties,
      sorts: (_updates$sorts = updates.sorts) !== null && _updates$sorts !== void 0 ? _updates$sorts : this.sorts,
      pipelinePropertyName: (_updates$pipelineProp = updates.pipelinePropertyName) !== null && _updates$pipelineProp !== void 0 ? _updates$pipelineProp : this.pipelinePropertyName,
      objectTypeId: (_updates$objectTypeId = updates.objectTypeId) !== null && _updates$objectTypeId !== void 0 ? _updates$objectTypeId : this.objectTypeId
    });
  }
  hash() {
    return generateHash(this);
  }

  /**
   * Converts a backend view into a Segment.
   * Validates the parsed data to ensure it matches expected schema.
   *
   * This method handles:
   * 1. Extracting relevant parts from the view state (pipelineId, search, sorts)
   * 2. Extracting and validating filter groups
   * 3. Parsing quick filters and their properties
   * 4. Applying migrations for backward compatibility
   * 5. Add legacy behaviors (around quick filters) as seen in index page
   *
   * Falls back to default values (i.e., empty filters) if parsing fails.
   *
   * @param view The backend view to convert
   * @returns A new Segment instance
   */
  static fromBEView(view, pipelinePropertyName) {
    var _parsedState$search;
    // parts of the view state that's relevant to the segment
    const viewStateSchema = t.object({
      pipelineId: t.optional(t.string),
      search: t.optional(t.string),
      sortKey: t.optional(t.union(t.string, t.array(t.string))),
      order: t.optional(t.union(t.literal(1), t.literal(-1)))
    });
    const parsedState = tryOrElse(() => parse(view.state, viewStateSchema), e => {
      console.error('Failed to parse state from BE view:', e);
      return {
        pipelineId: undefined,
        search: '',
        sortKey: undefined,
        order: undefined
      };
    });
    const advancedFilterGroups = tryOrElse(() => {
      var _parse;
      return (_parse = parse(view.filterGroups, t.array(filterGroupSchema))) !== null && _parse !== void 0 ? _parse : [];
    }, e => {
      console.error('Failed to parse filter groups from BE view:', e);
      return [];
    });
    const quickFilters = tryOrElse(() => parse(view.quickFilters, t.array(t.unknown)), e => {
      console.error('Failed to parse quick filters from BE view:', e);
      return [];
    });

    // Apply migration to both advanced filter groups and quick filters
    const migratedAdvancedFilterGroups = migrateFilters(advancedFilterGroups);
    const quickFilterProperties = tryOrElse(() => {
      var _parse2;
      return (_parse2 = parse(view.quickFilterProperties, t.optional(t.array(t.object({
        name: t.string
      }))))) !== null && _parse2 !== void 0 ? _parse2 : [];
    }, e => {
      console.error('Failed to parse quick filter properties from BE view:', e);
      return [];
    });

    // Convert BE sort format to FE sort format
    let sorts = [];
    if (parsedState.sortKey) {
      const properties = Array.isArray(parsedState.sortKey) ? parsedState.sortKey : [parsedState.sortKey];
      sorts = properties.map(property => ({
        property,
        order: parsedState.order === -1 ? 'ASC' : 'DESC'
      }));
    }
    return new Segment({
      advancedFilterGroups: migratedAdvancedFilterGroups,
      // quick filters are not migrated (same behavior as index page)
      quickFilters,
      search: (_parsedState$search = parsedState.search) !== null && _parsedState$search !== void 0 ? _parsedState$search : undefined,
      pipelineId: parsedState.pipelineId,
      quickFilterProperties,
      sorts,
      pipelinePropertyName,
      objectTypeId: view.objectTypeId
    });
  }

  /**
   * Converts this segment to a backend view format.
   * This method applies the segment's filtering, sorting, and search criteria to the provided backend view.
   *
   * @param beView The backend view to apply the segment data to
   * @returns A new backend view with segment properties applied
   * @throws Never throws - safely handles all conversion operations
   */
  toBEView(beView) {
    var _this$sorts$;
    return Object.assign({}, beView, {
      filterGroups: JSON.stringify(this.advancedFilterGroups),
      quickFilters: JSON.stringify(this.quickFilters),
      quickFilterProperties: JSON.stringify(this.quickFilterProperties),
      state: JSON.stringify(Object.assign({}, JSON.parse(beView.state || '{}'), {
        pipelineId: this.pipelineId === null ? undefined : this.pipelineId,
        // search is not stored in view
        // Convert FE sort format to BE sort format
        sortKey: this.sorts.length > 1 ? this.sorts.map(sort => sort.property) : (_this$sorts$ = this.sorts[0]) === null || _this$sorts$ === void 0 ? void 0 : _this$sorts$.property,
        order: this.sorts.length > 0 ? this.sorts[0].order === 'ASC' ? -1 : 1 : undefined
      }))
    });
  }

  /**
   * Loads a segment from the client cache using its hash identifier.
   *
   * This method is part of the draft system that enables client-side editing
   * without immediate backend persistence. It's used internally by the
   * `useSegment` hook when an edit hash is present to retrieve draft segments.
   *
   * ## Usage with Hooks
   *
   * Generally, you should not need to call this method directly. Instead:
   *
   * ```typescript
   * // The hook automatically handles draft loading from client cache
   * const { data: segment } = useSegment();
   * ```
   *
   * ## Error Handling
   *
   * When a segment can't be loaded from cache (e.g., hash doesn't exist,
   * storage errors), the `useSegment` hook will fall back to the backend view.
   *
   * @param hash The unique hash identifier for the segment
   * @param clientStorage Storage implementation for accessing client cache
   * @throws {Error} If segment is not found in cache
   * @throws {Error} If stored data cannot be parsed
   * @throws {Error} If storage access fails
   * @returns Promise resolving to the loaded Segment instance
   */
  static async fromClientCache(hash, clientStorage) {
    const segmentJson = await clientStorage.getItem(`segment-${hash}`);
    const parsedData = mapIfExistsOrThrow(segmentJson, parseCurried(segmentSchema), new Error('No segment draft found in client cache'));
    return new Segment(parsedData);
  }

  /**
   * Saves this segment to the client cache and returns its unique hash.
   *
   * This method is a core part of the draft system that enables client-side
   * editing without immediate backend persistence. It:
   *
   * 1. Generates a unique hash for this segment based on its content
   * 2. Stores the serialized segment in client storage under a key derived from the hash
   * 3. Returns the hash, which can be used later to retrieve the segment
   *
   * ## Usage with Hooks
   *
   * Generally, you should not need to call this method directly. Instead:
   *
   * ```typescript
   * const [saveSegment] = useMutateSegment();
   *
   * // This internally calls toClientCache and maintains the edit hash state
   * saveSegment(updatedSegment);
   * ```
   *
   * ## Storage Approach
   *
   * The segment is stored as a JSON string. The key is derived from the hash
   * using the format `segment-{hash}`. This system allows multiple drafts to
   * exist simultaneously without conflicting.
   *
   * @param clientStorage Storage implementation for storing in client cache
   * @throws {Error} If hash generation fails
   * @throws {Error} If storage access fails or quota is exceeded
   * @returns Promise resolving to the unique hash of the saved segment
   */
  async toClientCache(clientStorage) {
    const hash = await this.hash();
    const key = Segment.getClientCacheKey(hash);
    await Promise.all([clientStorage.setItem(key, JSON.stringify(this)), datesHash(key, Date.now(), clientStorage)]);
    return hash;
  }

  /**
   * Generates the storage key used for storing/retrieving segments in client storage.
   *
   * This utility method ensures consistent key formatting between save and load operations.
   *
   * @param hash The segment hash to generate a key for
   * @returns The formatted storage key (format: 'segment-{hash}')
   */
  static getClientCacheKey(hash) {
    return `segment-${hash}`;
  }

  /**
   * # Use this with CRM Search API
   *
   * ```typescript
   * // For CRM Search API (no XO filters) - use the getter instead:
   * const filters = segment.crmSearchFilters;
   *
   * // For advanced usage with XO filters:
   * const filtersWithXO = segment.consolidatedFilters({}, undefined, true);
   * ```
   *
   * ## Timezone Handling
   *
   * Timezone-aware timestamps are automatically handled internally using I18n.moment.userTz().
   *
   * @param enabledXOFilter Whether to include cross-object filters in the result (default: false)
   * @returns Filter groups array ready for use with CRM Search API
   */
  consolidatedFilters(___unused = {}, __unused, enabledXOFilter = false) {
    // Generate current timestamp for __hs__NOW placeholder replacement
    const placeholders = {
      __hs__NOW: I18n.moment.userTz().valueOf()
    };
    const normalizedFilterGroups = this._advancedFilterGroups.length > 0 ? this._advancedFilterGroups : [{
      filters: [],
      crossObjectFilters: []
    }];
    return normalizedFilterGroups.map(group => {
      const {
        filters
      } = group;

      // First replace any placeholders in the filters
      let consolidatedFilters = [...filters]
      // Remove legacy 'default' property if it exists
      .map(filter => {
        const f = filter;
        if ('default' in f) {
          const filterWithoutDefault = _objectWithoutPropertiesLoose(f, _excluded);
          return filterWithoutDefault;
        }
        return filter;
      })
      // Replace placeholders if provided
      .map(filter => {
        const f = filter;
        if ('value' in f) {
          if (f.value === '__hs__NOW') {
            return Object.assign({}, f, {
              value: placeholders.__hs__NOW
            });
          }
        }
        if ('values' in f) {
          return Object.assign({}, f, {
            values: f.values.map(value => {
              return value === '__hs__NOW' ? placeholders.__hs__NOW : value;
            })
          });
        }
        return f;
      });

      // Add pipeline filter if pipelineId exists and should include pipeline filter
      if (this.shouldIncludePipelineFilter) {
        consolidatedFilters = consolidatedFilters.concat({
          operator: 'EQ',
          value: this.pipelineId,
          property: this.pipelinePropertyName
        });
      }

      // Add quick filters to each group (quick filters are ANDed to each group)
      const allFilters = consolidatedFilters.concat(this.quickFilters);
      return {
        filters: allFilters,
        crossObjectFilters: enabledXOFilter ? group.crossObjectFilters : undefined
      };
    });
  }

  /**
   * # Use this for API calls (crm search, etc);
   * Does not support XO filters.
   *
   * ## **WARNING:** This gets timestamp internally (I18n.moment.userTz().valueOf()) . Always mock timer!
   *
   * ```typescript
   * expect(segment.crmSearchFilters).toEqual(segment.crmSearchFilters) // MIGHT BE FALSE!!!
   * ```
   *
   * @see Segment.consolidatedFilters()
   */
  get crmSearchFilters() {
    return this.consolidatedFilters();
  }
}